import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import { OptionsBottomDrawer } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { useOpenCallContactModal } from 'src/components/pages/conversations/components/open-legacy-call-modal-icon';
import { openRemoteModalWrapper } from 'src/components/pages/legacy/components/open-legacy-remote-modal-link';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useUrls } from 'src/hooks/use-urls';
import { showContactStatusModal } from './contact-status-modal';

type ContactOptionsBottomDrawerProps = {
  contact?: Contact;
  visible: boolean;
  onClose: VoidFunction;
};

export const ContactOptionsBottomDrawer = memo<ContactOptionsBottomDrawerProps>(
  ({ contact, visible, onClose }) => {
    const { getLegacyContactFollowUpUrl, getContactModalUrl } = useUrls();
    const { t } = useTranslation(['contacts', 'common']);
    const { getFeatureFlag } = useAuthorization();
    const hasCrmFeatureFlag = getFeatureFlag('crm');
    const openCallContactModal = useOpenCallContactModal();
    const history = useHistory();

    const bottomDrawerOptions = useMemo(() => {
      if (!contact) {
        return [];
      }

      const options = [
        {
          label: t('contacts:contactOptions.view'),
          icon: 'magnifyingGlass',
          onClick: () => {
            history.push(getContactModalUrl({ contactId: contact.id }));
          },
        },
      ];

      if (hasCrmFeatureFlag) {
        options.push({
          label: t('contacts:contactOptions.updateStatus'),
          icon: 'status',
          onClick: () => {
            showContactStatusModal(contact.id);
          },
        });
      }

      if (contact?.primaryPhone) {
        options.push({
          label: t('contacts:contactOptions.call'),
          icon: 'call',
          onClick: () => {
            void openCallContactModal(contact.id);
          },
        });
      }

      if (contact?.primaryEmail) {
        options.push({
          label: t('contacts:contactOptions.sendEmail'),
          icon: 'email',
          onClick: () => {
            window.location.href = `mailto:${contact.primaryEmail}`;
          },
        });
      }

      if (hasCrmFeatureFlag) {
        options.push({
          label: t('contacts:contactOptions.followUp'),
          icon: 'contactSetting',
          onClick: () => {
            openRemoteModalWrapper(getLegacyContactFollowUpUrl(contact.id));
          },
        });
      }

      return options;
    }, [
      t,
      hasCrmFeatureFlag,
      contact,
      history,
      getContactModalUrl,
      openCallContactModal,
      getLegacyContactFollowUpUrl,
    ]);

    return (
      <OptionsBottomDrawer
        visible={visible}
        title={contact?.label ?? ''}
        onClose={onClose}
        options={bottomDrawerOptions}
      />
    );
  },
);
